import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { VictoryPie } from 'victory'
import { CircleLabelNumber, ColorDash } from '../../components/ReportPieChartDecorations'
import { formatMoney } from '../../helpers/utils'
import { useReportPieChartMetrics } from '../helpers/useReportPieChartMetrics'
import { REPORT_CHART_CARD_SX } from './constants/reportMotiveCard'
import {
  REPORT_PAYMENT_COLORS,
  REPORT_PAYMENT_NUMBER_FORMAT_RULES,
  REPORT_PAYMENT_TEXT_MAPPINGS
} from './constants/reportPayment'

const formatNumber = (number) => {
  for (const rule of REPORT_PAYMENT_NUMBER_FORMAT_RULES) {
    if (number < rule.limit) return rule.format(number)
  }

  return `${Math.floor(number / 100_000) / 10}M`
}

const getModifiedText = (text) => {
  return REPORT_PAYMENT_TEXT_MAPPINGS[text] || text
}

const ReportPayment = ({ reportPayment, selectedTab, displayTab, compact = false }) => {
  const { isMobileLayout, chartSize, innerRadius, radius, legendMaxHeight, gridGap } =
    useReportPieChartMetrics({ compact })
  const totalValue = reportPayment.reduce((sum, motive) => sum + motive.value, 0)

  const [numberValue, setNumberValue] = useState(totalValue)
  const [hoverIndex, setHoverIndex] = useState(-1)

  const buildReportPayment = () => {
    return reportPayment
      .filter((motive) => motive.value > 0)
      .map((motive) => ({
        x: motive.text,
        y: motive.value,
        color: REPORT_PAYMENT_COLORS[motive.text] || '#000000'
      }))
  }

  const sortedReportPayment = () => {
    return [...reportPayment]
      .filter((motive) => motive.value > 0)
      .map((motive, index) => ({
        ...motive,
        originalIndex: index
      }))
      .sort((a, b) => b.value - a.value)
  }

  useEffect(() => {
    setNumberValue(totalValue)
  }, [selectedTab, totalValue])

  if (selectedTab !== 1 && !displayTab.includes(selectedTab)) {
    return null
  }

  return (
    <Card elevation={0} sx={REPORT_CHART_CARD_SX}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: isMobileLayout ? 2 : 1.75,
          '&:last-child': { pb: isMobileLayout ? 2 : 1.75 }
        }}
      >
        <Typography
          sx={{
            fontSize: isMobileLayout ? 14 : 13,
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#334155',
            mb: isMobileLayout ? 2 : 1.25
          }}
        >
          Métodos de pago usados
        </Typography>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: isMobileLayout
              ? '1fr'
              : `${chartSize}px minmax(0, 1fr)`,
            gap: gridGap,
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: chartSize,
              height: chartSize,
              mx: 'auto'
            }}
          >
            <VictoryPie
              width={chartSize}
              height={chartSize}
              style={{ labels: { fill: 'black' } }}
              padAngle={2}
              colorScale={buildReportPayment().map((motive) => motive.color)}
              innerRadius={innerRadius}
              radius={radius}
              labels={() => ''}
              data={buildReportPayment()}
            />
            <CircleLabelNumber number={numberValue} formatDisplay={formatNumber} />
          </Box>

          <Box
            sx={{
              minHeight: 0,
              maxHeight: legendMaxHeight,
              overflowY: 'auto',
              pr: 0.5
            }}
          >
            {sortedReportPayment().map((motive, index) => {
              const color = REPORT_PAYMENT_COLORS[motive.text] || '#000000'
              const selected = hoverIndex === motive.originalIndex

              return (
                <Box
                  key={index}
                  onMouseOver={() => {
                    setNumberValue(motive.value)
                    setHoverIndex(motive.originalIndex)
                  }}
                  onMouseLeave={() => {
                    setNumberValue(totalValue)
                    setHoverIndex(-1)
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.5,
                    py: 0.35,
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <ColorDash color={color} selected={selected} />

                  <Typography
                    sx={{
                      minWidth: 78,
                      fontSize: isMobileLayout ? 13 : 12,
                      fontWeight: 700,
                      color: '#111827',
                      textAlign: 'right'
                    }}
                  >
                    {formatMoney(motive.value)}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isMobileLayout ? 13 : 12,
                      color: '#0F172A',
                      lineHeight: 1.35,
                      minWidth: 0,
                      flex: 1,
                      wordBreak: 'break-word'
                    }}
                  >
                    {getModifiedText(motive.text)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default ReportPayment
